import { queryClient } from "@/App";
import Trabalho from "@/Models/Sistema/Trabalho";
import TrabalhoLog from "@/Models/Sistema/Trabalho/TrabalhoLog";
import { EnumTrabalhoTipo } from "@/Models/Sistema/Trabalho/TrabalhoTipo";
import { IPagina, PaginaInfo } from "@/Models/Utils/Pagina/Pagina";
import { Axios } from "@/Services/Axios";
import RequestUtilitario, { MutationBasicaParam, QueryBasicaParam } from "@/Utils/RequisicaoUtilitario";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { Dayjs } from "dayjs"

// export function tipoParaServico({codTrabalhoTipo}:{ codTrabalhoTipo:EnumTrabalhoTipo }) {
//     let entidade = "";
//     switch (codTrabalhoTipo)
//     {
//         case EnumTrabalhoTipo.ConciliacaoGetNet:
//             entidade = "ConciliacaoGetNet";
//             break;
//         default:
//             entidade = "ConciliacaoAuttar";
//             break;
//     }

//     return entidade;
// }
export function useConciliacaoAuttarAdicionar({
    onSuccess,
    queryKey,
    codTrabalhoTipo,
    ...rest
}: MutationBasicaParam<File, AxiosResponse<void>, { codTrabalhoTipo:EnumTrabalhoTipo }>) {

    return useMutation({
        ...rest,
        mutationFn : (data) => {            
            const formData = new FormData();
            formData.append("arquivo", data);

            const opcoes = {
                headers : {
                    "Content-Type" : "multipart/form-data"
                }
            };

            return Axios.post<void>(`Financeiro/Conta/ConciliacaoGetNet/${codTrabalhoTipo}`, formData, opcoes);
        },
        onSuccess : (...data) => {
            queryClient.invalidateQueries({
                queryKey: [ "Domain", "Sistema", "Trabalho", codTrabalhoTipo ]
            });
            onSuccess?.(...data);
        },
    });
}

export function useConciliacaoAuttarDesativar({
    onSuccess,
    codTrabalhoTipo,
    queryKey,
    ...rest
}: MutationBasicaParam<Trabalho, AxiosResponse<void>, { codTrabalhoTipo:EnumTrabalhoTipo }>) {

    return useMutation({
        ...rest,
        mutationFn : (data) => { 
            return Axios.delete<void>(`Financeiro/Conta/ConciliacaoGetNet/${data.codigo}`);
        },
        onSuccess : (...data) => {
            queryClient.invalidateQueries({
                queryKey: [ "Domain", "Sistema", "Trabalho", codTrabalhoTipo ]
            });
            onSuccess?.(...data);
        },
    });
}

export function useConciliacaoAuttarPaginar({
    pagina,
    trabalho,
    ativo,
    dataInical,
    dataFinal,
    onFinish,
    codTrabalhoTipo,
    ...rest
}:QueryBasicaParam<AxiosResponse<IPagina<Trabalho>>, IPagina<Trabalho>, {
    pagina:PaginaInfo;
    trabalho?:Trabalho;
    ativo?:boolean;
    dataInical?:Date|Dayjs;
    dataFinal?:Date|Dayjs;
    codTrabalhoTipo:EnumTrabalhoTipo;
    onFinish?:() => void;
}>) {

    const queryString = RequestUtilitario.objectToQueryString({
        ...(pagina ?? new PaginaInfo()),
        ...(trabalho ?? {}),
        ativo,
        dataInical,
        dataFinal,
        codTrabalhoTipo
    });

    return useQuery({
        ...rest,
        queryKey : [ "Domain", "Sistema", "Trabalho", codTrabalhoTipo, queryString ],
        queryFn : () => Axios.get<IPagina<Trabalho>>(`Financeiro/Conta/ConciliacaoGetNet/pagina?${queryString}`).finally(onFinish),
        select(data) {
            const pagina = data.data;

            pagina.registros = pagina.registros.map(m => {
                m.dataOcorrencia = new Date(m.dataOcorrencia as any);
                m.dataCriacao = new Date(m.dataCriacao as any);
                return m;
            });

            return pagina;
        },
        staleTime : 1000 * 60 * 0.3
    });
}

export function useConciliacaoAuttarLogsPaginar({
    pagina,
    trabalhoLog,
    ativo,
    onFinish,
    codTrabalhoTipo,
    ...rest
}:QueryBasicaParam<AxiosResponse<IPagina<TrabalhoLog>>, IPagina<TrabalhoLog>, {
    pagina:PaginaInfo;
    trabalhoLog?:TrabalhoLog;
    codTrabalhoTipo:EnumTrabalhoTipo;
    ativo?:boolean;
    onFinish?:() => void;
}>) {

    const queryString = RequestUtilitario.objectToQueryString({
        ...(pagina ?? new PaginaInfo()),
        ...(trabalhoLog ?? {}),
        ativo
    });

    return useQuery({
        ...rest,
        queryKey : [ "Domain", "Sistema", "Trabalho", codTrabalhoTipo, "Logs", "Pagina", queryString ],
        queryFn : () => Axios.get<IPagina<TrabalhoLog>>(`Financeiro/Conta/ConciliacaoGetNet/logs/pagina?${queryString}`).finally(onFinish),
        select(data) {
            const pagina = data.data;

            pagina.registros = pagina.registros.map(m => {
                m.dataCriacao = new Date(m.dataCriacao as any);
                return m;
            });

            return pagina;
        },
        staleTime : 1000 * 60 * 0.3
    });
}

export function useConciliacaoAuttarBaixarArquivo({
    codTrabalhoTipo,
    ...rest
}:MutationBasicaParam<{
    codTrabalho : number;
}, AxiosResponse<Blob>, {
    codTrabalhoTipo:EnumTrabalhoTipo;
}>) {

    return useMutation({
        ...rest,
        mutationFn: ({ codTrabalho }) => Axios.get<Blob>(`Financeiro/Conta/ConciliacaoGetNet/${codTrabalho}/baixar-arquivo`, {
            responseType: "blob"
        } as AxiosRequestConfig<any>)
    });
}

export function useConciliacaoAuttarLogDetalhe({
    codTrabalho,
    linha,
    onFinish,
    codTrabalhoTipo,
    ...rest
}:QueryBasicaParam<AxiosResponse<Array<[string, string]>>, AxiosResponse<Array<[string, string]>>, {
    codTrabalho : number;
    linha : number;
    onFinish?:() => void;
    codTrabalhoTipo:EnumTrabalhoTipo;
}>) {

    return useQuery({
        ...rest,
        queryKey : [ "Domain", "Sistema", "Trabalho", codTrabalhoTipo, codTrabalho, "Log", linha ],
        queryFn : () => Axios.get<Array<[string, string]>>(`Financeiro/Conta/ConciliacaoGetNet/${codTrabalho}/arquivo/${linha}/linha`).finally(onFinish),
        staleTime : 1000 * 60 * 0.3
    });
}